import { lazy, Suspense, type ReactElement } from "react";
import { Routes, Route, Navigate, useParams } from "react-router-dom";
import { AppShell } from "@/components/AppShell";
import { ComingSoon } from "@/components/ComingSoon";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { OfflineBanner } from "@/components/OfflineBanner";
import { DialogRoot } from "@/components/Dialog";
import { AuthGate } from "@/features/auth/AuthGate";

/**
 * Phase 7: every route is lazy-loaded + wrapped in an ErrorBoundary so a
 * crash in one screen never takes the whole shell down. The Suspense
 * fallback is a thin progress bar — Vite chunks each route-level import()
 * into its own JS bundle, so initial load only pulls what the current
 * URL needs.
 */

const DashboardScreen = lazy(() =>
  import("@/features/dashboard/DashboardScreen").then((m) => ({
    default: m.DashboardScreen,
  })),
);
const ClientListScreen = lazy(() =>
  import("@/features/crm/ClientListScreen").then((m) => ({
    default: m.ClientListScreen,
  })),
);
const PipelineScreen = lazy(() =>
  import("@/features/crm/PipelineScreen").then((m) => ({
    default: m.PipelineScreen,
  })),
);
const QuoteListScreen = lazy(() =>
  import("@/features/quotes/QuoteListScreen").then((m) => ({
    default: m.QuoteListScreen,
  })),
);
const QuoteEditorScreen = lazy(() =>
  import("@/features/quotes/QuoteEditorScreen").then((m) => ({
    default: m.QuoteEditorScreen,
  })),
);
const OperationsHome = lazy(() =>
  import("@/features/operations/OperationsHome").then((m) => ({
    default: m.OperationsHome,
  })),
);
const SuppliersScreen = lazy(() =>
  import("@/features/operations/SuppliersScreen").then((m) => ({
    default: m.SuppliersScreen,
  })),
);
const PurchaseOrdersScreen = lazy(() =>
  import("@/features/operations/PurchaseOrdersScreen").then((m) => ({
    default: m.PurchaseOrdersScreen,
  })),
);
const POEditorScreen = lazy(() =>
  import("@/features/operations/POEditorScreen").then((m) => ({
    default: m.POEditorScreen,
  })),
);
const DeliveryDocketsScreen = lazy(() =>
  import("@/features/operations/DeliveryDocketsScreen").then((m) => ({
    default: m.DeliveryDocketsScreen,
  })),
);
const DocketEditorScreen = lazy(() =>
  import("@/features/operations/DocketEditorScreen").then((m) => ({
    default: m.DocketEditorScreen,
  })),
);
const ProjectListScreen = lazy(() =>
  import("@/features/projects/ProjectListScreen").then((m) => ({
    default: m.ProjectListScreen,
  })),
);
const ProjectDetailScreen = lazy(() =>
  import("@/features/projects/ProjectDetailScreen").then((m) => ({
    default: m.ProjectDetailScreen,
  })),
);
const InvoicesScreen = lazy(() =>
  import("@/features/projects/InvoicesScreen").then((m) => ({
    default: m.InvoicesScreen,
  })),
);
const CatalogueScreen = lazy(() =>
  import("@/features/catalogue/CatalogueScreen").then((m) => ({
    default: m.CatalogueScreen,
  })),
);
const SettingsScreen = lazy(() =>
  import("@/features/settings/SettingsScreen").then((m) => ({
    default: m.SettingsScreen,
  })),
);

/** Wrap a lazy-loaded screen with Suspense fallback + ErrorBoundary. */
function Safe({ label, children }: { label: string; children: ReactElement }) {
  return (
    <ErrorBoundary label={`${label} crashed`}>
      <Suspense fallback={<RouteLoading label={label} />}>{children}</Suspense>
    </ErrorBoundary>
  );
}

function RouteLoading({ label }: { label: string }) {
  return (
    <div
      className="p-10 text-center text-sm"
      style={{ color: "var(--neo-text-muted)" }}
    >
      <div
        className="mx-auto mb-3"
        style={{
          width: 36,
          height: 36,
          border: "3px solid var(--neo-bg3)",
          borderTopColor: "var(--neo-teal)",
          borderRadius: "50%",
          animation: "spin .8s linear infinite",
        }}
      />
      Loading {label.toLowerCase()}…
    </div>
  );
}

export function App() {
  return (
    <div className="min-h-screen flex flex-col">
      <OfflineBanner />
      <DialogRoot />
      <AuthGate>
        <Routes>
          <Route element={<AppShell />}>
            <Route
              index
              element={
                <Safe label="Dashboard">
                  <DashboardScreen />
                </Safe>
              }
            />

            {/* CRM */}
            <Route
              path="/crm"
              element={
                <Safe label="Clients">
                  <ClientListScreen />
                </Safe>
              }
            />
            <Route
              path="/crm/pipeline"
              element={
                <Safe label="Pipeline">
                  <PipelineScreen />
                </Safe>
              }
            />
            <Route path="/crm/:clientId" element={<LegacyClientRedirect />} />

            {/* Quotes */}
            <Route
              path="/quotes"
              element={
                <Safe label="Quotes">
                  <QuoteListScreen />
                </Safe>
              }
            />
            <Route
              path="/quotes/:quoteId"
              element={
                <Safe label="Quote editor">
                  <QuoteEditorScreen />
                </Safe>
              }
            />

            {/* Projects */}
            <Route
              path="/projects"
              element={
                <Safe label="Projects">
                  <ProjectListScreen />
                </Safe>
              }
            />
            <Route
              path="/projects/:projectId"
              element={
                <Safe label="Project">
                  <ProjectDetailScreen />
                </Safe>
              }
            />

            {/* Invoices — org-wide register across all projects */}
            <Route
              path="/invoices"
              element={
                <Safe label="Invoices">
                  <InvoicesScreen />
                </Safe>
              }
            />

            {/* Operations */}
            <Route
              path="/operations"
              element={
                <Safe label="Operations">
                  <OperationsHome />
                </Safe>
              }
            />
            <Route
              path="/operations/suppliers"
              element={
                <Safe label="Suppliers">
                  <SuppliersScreen />
                </Safe>
              }
            />
            <Route
              path="/operations/pos"
              element={
                <Safe label="Purchase Orders">
                  <PurchaseOrdersScreen />
                </Safe>
              }
            />
            <Route
              path="/operations/pos/:poId"
              element={
                <Safe label="PO editor">
                  <POEditorScreen />
                </Safe>
              }
            />
            <Route
              path="/operations/dockets"
              element={
                <Safe label="Delivery Dockets">
                  <DeliveryDocketsScreen />
                </Safe>
              }
            />
            <Route
              path="/operations/dockets/:docketId"
              element={
                <Safe label="Docket editor">
                  <DocketEditorScreen />
                </Safe>
              }
            />

            {/* Catalogue */}
            <Route
              path="/catalogue"
              element={
                <Safe label="Catalogue">
                  <CatalogueScreen />
                </Safe>
              }
            />

            {/* Workspace settings */}
            <Route
              path="/settings"
              element={
                <Safe label="Settings">
                  <SettingsScreen />
                </Safe>
              }
            />

            <Route
              path="/drawings"
              element={
                <ComingSoon
                  title="Drawings"
                  phase="Per-quote"
                  description="Drawings live inside each quote. Open a quote and go to the Drawings tab to upload floor plans (PDF / PNG / JPG), mark fixtures manually, or run AI Takeoff."
                />
              }
            />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Route>
        </Routes>
      </AuthGate>
    </div>
  );
}

// ---------------------------------------------------------------------
// Legacy deep-link: /crm/:clientId → /crm/pipeline?client=:clientId
// ---------------------------------------------------------------------
function LegacyClientRedirect() {
  const { clientId } = useParams<{ clientId: string }>();
  return (
    <Navigate
      to={clientId ? `/crm/pipeline?client=${clientId}` : "/crm"}
      replace
    />
  );
}
